import React, {
    useCallback, useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import {
    Box, Typography, useTheme 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import ProgressBar from '../../components/progressBar';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';

type Props = {
    position: number
};

export const DrinkBaselineOutro = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const drinkBaselines = useMemo(() => {
        const answers = formContext.answers;
        return [
            answers.mon_baseline,
            answers.tue_baseline,
            answers.wed_baseline,
            answers.thu_baseline,
            answers.fri_baseline,
            answers.sat_baseline,
            answers.sun_baseline
        ];
    }, [
        formContext.answers.mon_baseline,
        formContext.answers.tue_baseline,
        formContext.answers.wed_baseline,
        formContext.answers.thu_baseline,
        formContext.answers.fri_baseline,
        formContext.answers.sat_baseline,
        formContext.answers.sun_baseline
    ]);

    const [daysHeight, setDaysHeight] = useState(null);
    const [dayHeight, setDayHeight] = useState(null);
    const [skipAnimation, setSkipAnimation] = useState(false);
    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflowY: 'scroll',
            overflowX: 'hidden',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            height: '100%',
        },
        title: {
            position: 'relative',
            marginBottom: '24px',
            [theme.breakpoints.up('lg')]: {
                textAlign: 'center'
            }
        },
        days: {
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '12px',
            overflow: 'hidden',
            rowGap: '1px',
            margin: '0 auto',
            animation: `$fadeOut ${skipAnimation ? 0 : 0.2}s ease-in-out ${skipAnimation ? 0 : 1}s forwards`
        },
        day: {
            width: '100%',
            fontWeight: TypographyWeights.MEDIUM,
            margin: 0,
            padding: '0 16px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            ['& p']: {
                margin: 0
            }
        },
        dayText: {
            lineHeight: '40px'
        },
        summaryContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: '16px',
            maxWidth: '100%',
            margin: '0 auto',
            marginTop: '30px',
            animation: `$shift ${skipAnimation ? 0 : 0.5}s ease-in-out ${skipAnimation ? 0 : 1}s forwards`
        },
        summaryBox: {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            borderRadius: '32px 8px 8px 32px'
        },
        summaryText: {
            padding: '0 12px',
            backgroundColor: theme.palette.secondary['100'],
            lineHeight: '46px',
            width: '100%',
            height: '100%',
            marginLeft: '32px',
            paddingLeft: '56px',
            borderRadius: '32px 8px 8px 32px'
        },
        summaryValue: {
            backgroundColor: theme.palette.secondary.main,
            fontWeight: TypographyWeights.BOLD,
            color: theme.palette.white,
            borderRadius: '50%',
            border: `4px solid ${theme.palette.white}`,
            position: 'absolute',
            width: '64px',
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        tipBox: {
            maxWidth: '100%',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            rowGap: '2px',
            transform: 'translate(0, 100vh)',
            opacity: 0,
            height: '208px',
            animation: `$fadeIn ${skipAnimation ? 0 : 0.25}s ease-in-out ${skipAnimation ? 0 : 1.75}s forwards, $slideUp ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`
        },
        tipBodyBox: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px',
            backgroundColor: theme.palette.secondary['100'],
            padding: '16px',
            borderRadius: '8px',
        },
        tipBody1: {
            fontWeight: TypographyWeights.BOLD
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: '1',
                pointerEvents: 'auto' 
            },
            '100%': {
                opacity: '0',
                pointerEvents: 'none' 
            },
        },
        '@keyframes shift': {
            '0%': {
                transform: 'translate(0)' 
            },
            '100%': {
                transform: `translate(0, -${daysHeight + 32}px)` 
            },
        },
        '@keyframes slideUp': {
            '0%': {
                transform: 'translate(0, 100vh)' 
            },
            '100%': {
                transform: `translate(0, -${daysHeight}px)` 
            },
        }
    })), [daysHeight, dayHeight, skipAnimation]);
    const classes = useStyles();
    const theme = useTheme();

    const dayColors = drinkBaselines.map((baseline) => {
        if (baseline === 0) return {
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.neutral.main 
        };
        if (baseline <= 3) return {
            backgroundColor: theme.palette.secondary['100'],
            color: theme.palette.neutral.main 
        };
        if (baseline <= 6) return {
            backgroundColor: theme.palette.secondary['200'],
            color: theme.palette.neutral.main 
        };
        else return {
            backgroundColor: theme.palette.secondary['300'],
            color: theme.palette.neutral.main 
        };
    });

    const onClick = () => {
        if (!skipAnimation) setSkipAnimation(true);
        else formContext.goNext();
    };

    const totalDrinks = useMemo(() => drinkBaselines.reduce((sum, baseline) => sum + baseline, 0), [drinkBaselines]);
    const totalDryDays = useMemo(() => drinkBaselines.reduce((sum, baseline) => sum + (baseline === 0 ? 1 : 0), 0), [drinkBaselines]);

    const getCoachNote = useCallback(() => {
        const heavyDrinkingDays = drinkBaselines.reduce((sum, baseline) => sum + (baseline > 4 ? 1 : 0), 0);
        const drinkingDays = 7 - drinkBaselines.reduce((sum, baseline) => sum + (baseline === 0 ? 1 : 0), 0);
        const averageDrinksPerDay = Math.ceil(drinkBaselines.reduce((sum, baseline) => sum + baseline, 0) / 7);
        const outlierDays = drinkBaselines.reduce((sum, baseline) => sum + (baseline >= averageDrinksPerDay + 2 ? 1 : 0), 0);

        let body1 = '';
        let body2 = '';
        let userPercentage = 89;

        if (totalDrinks >= 1 && totalDrinks <= 5) { userPercentage = 99; }
        else if (totalDrinks >= 6 && totalDrinks <= 10) { userPercentage = 89; }
        else if (totalDrinks >= 11 && totalDrinks <= 15) { userPercentage = 63; }
        else if (totalDrinks >= 16 && totalDrinks <= 20) { userPercentage = 54; }
        else { userPercentage = 50; }

        if (heavyDrinkingDays > 3 && drinkingDays === 7) {
            body1 = '57% of Sunnyside members join us as daily drinkers, so this is a pretty typical starting point.';
            body2 = 'We\'ll personalize your plan to reduce heavy drinking days and eventually add dry days to get you right where you want to be!';
        } else if (heavyDrinkingDays >= 1 && heavyDrinkingDays <= 3 && drinkingDays === 7) {
            body1 = '57% of Sunnyside members join us as daily drinkers with occasional heavier drinking days, so this is a pretty typical starting point.';
            body2 = 'We\'ll personalize your plan to reduce heavy drinking days and introduce dry days to get you right where you want to be!';
        } else if (heavyDrinkingDays === 0 && drinkingDays === 7) {
            body1 = '57% of Sunnyside members join us as daily drinkers, so this is a pretty typical starting point.';
            body2 = 'We\'ll personalize your plan to prioritize adding dry days each week, setting you up to get to where you want to be!';
        } else if (drinkingDays >= 4 && drinkingDays <= 6 && outlierDays >= 1) {
            body1 = `${drinkingDays === 4 ? '89' : drinkingDays === 5 ? '79' : '68'}% of Sunnyside members join us with ${drinkingDays} or more drinking days per week, with occasional heavier drinking days.`;
            body2 = 'This is a pretty typical starting point. We\'ll personalize your plan to reduce heavy drinking days and introduce dry days to get you right where you want to be!';
        } else if (drinkingDays >= 4 && drinkingDays <= 6 && outlierDays === 0) {
            body1 = `${drinkingDays === 4 ? '89' : drinkingDays === 5 ? '79' : '68'}% of Sunnyside members join us with ${drinkingDays} or more drinking days per week, so this is a pretty typical starting point.`;
            body2 = 'We\'ll personalize your plan to reduce heavy drinking days and eventually add dry days to get you right where you want to be!';
        } else if (drinkingDays >= 1 && drinkingDays <= 4 && outlierDays >= 1) {
            body1 = `${userPercentage}% of Sunnyside members join us at ${totalDrinks} or more total drinks per week, with occasional heavier drinking days.`;
            body2 = 'This is a pretty typical starting point. We\'ll personalize your plan to reduce heavy drinking days and overall drinks per week to get you right where you want to be!';
        } else if (drinkingDays >= 1 && drinkingDays <= 4 && outlierDays === 0) {
            body1 = `${userPercentage}% of Sunnyside members join us at ${totalDrinks} or more total drinks per week, so this is a pretty typical starting point.`;
            body2 = 'We\'ll personalize your plan to reduce overall drinks per week to get you right where you want to be!';
        } else if (drinkingDays === 0) {
            body1 = 'Starting from a place of zero drinks per week is an incredible accomplishment. You\'re already in a great place!';
            body2 = 'We\'ll work with you to maintain this pattern for the long-term, with the support of coaching and our incredible member community.';
        }

        return {
            body1,
            body2 
        };
    }, [drinkBaselines]);

    const {
        body1, body2 
    } = getCoachNote();

    const days = useRef(null);

    useEffect(() => {
        if (!days.current) return;
        setDaysHeight(days.current.clientHeight);
    }, [days.current]);

    const mondayBaseline = useRef(null);
    useEffect(() => {
        if (!mondayBaseline.current) return;

        setDayHeight(mondayBaseline.current.clientHeight);
    }, [mondayBaseline.current]);

    useEffect(() => {
        setTimeout(() => {
            setSkipAnimation(true);
        }, 2000);
    });

    return (
        <CardContainer
            sectionKey={'habits'}
            goBack={formContext.goBack}
            goNext={onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position}
                    sectionKey={'habits'}
                />
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <Typography
                            variant={TypographyVariants.H3}
                            className={classes.title}
                            dangerouslySetInnerHTML={{
                                __html: 'Based on your answers, here\'s what a typical week looks like for you today:' 
                            }}
                        />
                        <Box
                            className={classes.days}
                            ref={days}
                        >
                            <Box
                                ref={mondayBaseline}
                                className={classes.day}
                                style={dayColors[0]}
                            >
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.dayText}
                                >
                                    Monday
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.dayText}
                                    style={{
                                        fontWeight: TypographyWeights.BOLD 
                                    }}
                                >
                                    {drinkBaselines[0] || 0}
                                </Typography>
                            </Box>
                            <Box
                                className={classes.day}
                                style={dayColors[1]}
                            >
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.dayText}
                                >
                                    Tuesday
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.dayText}
                                    style={{
                                        fontWeight: TypographyWeights.BOLD 
                                    }}
                                >
                                    {drinkBaselines[1] || 0}
                                </Typography>
                            </Box>
                            <Box
                                className={classes.day}
                                style={dayColors[2]}
                            >
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.dayText}
                                >
                                    Wednesday
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.dayText}
                                    style={{
                                        fontWeight: TypographyWeights.BOLD 
                                    }}
                                >
                                    {drinkBaselines[2] || 0}
                                </Typography>
                            </Box>
                            <Box
                                className={classes.day}
                                style={dayColors[3]}
                            >
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.dayText}
                                >
                                    Thursday
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.dayText}
                                    style={{
                                        fontWeight: TypographyWeights.BOLD 
                                    }}
                                >
                                    {drinkBaselines[3] || 0}
                                </Typography>
                            </Box>
                            <Box
                                className={classes.day}
                                style={dayColors[4]}
                            >
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.dayText}
                                >
                                    Friday
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.dayText}
                                    style={{
                                        fontWeight: TypographyWeights.BOLD 
                                    }}
                                >
                                    {drinkBaselines[4] || 0}
                                </Typography>
                            </Box>
                            <Box
                                className={classes.day}
                                style={dayColors[5]}
                            >
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.dayText}
                                >
                                    Saturday
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.dayText}
                                    style={{
                                        fontWeight: TypographyWeights.BOLD 
                                    }}
                                >
                                    {drinkBaselines[5] || 0}
                                </Typography>
                            </Box>
                            <Box
                                className={classes.day}
                                style={dayColors[6]}
                            >
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.dayText}
                                >
                                    Sunday
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.dayText}
                                    style={{
                                        fontWeight: TypographyWeights.BOLD 
                                    }}
                                >
                                    {drinkBaselines[6] || 0}
                                </Typography>
                            </Box>
                        </Box>
                        <Box>
                            <Box className={classes.summaryContainer}>
                                <Box className={classes.summaryBox}>
                                    <Typography
                                        variant={TypographyVariants.H2}
                                        className={classes.summaryValue}
                                    >
                                        {totalDrinks || 0}
                                    </Typography>
                                    <Typography
                                        variant={TypographyVariants.H5}
                                        className={classes.summaryText}
                                    >
                                        Total Weekly 
                                        {' '}
                                        <span
                                            style={{
                                                fontWeight: TypographyWeights.BOLD 
                                            }}
                                        >
                                            Drinks
                                        </span>
                                    </Typography>
                                </Box>
                                <Box className={classes.summaryBox}>
                                    <Typography
                                        variant={TypographyVariants.H2}
                                        className={classes.summaryValue}
                                    >
                                        {totalDryDays || 0}
                                    </Typography>
                                    <Typography
                                        variant={TypographyVariants.H5}
                                        className={classes.summaryText}
                                    >
                                        Total Weekly 
                                        {' '}
                                        <span
                                            style={{
                                                fontWeight: TypographyWeights.BOLD 
                                            }}
                                        >
                                            Dry Days
                                        </span>
                                    </Typography>
                                </Box>
                            </Box>
                            {body1 && (
                                <Box className={classes.tipBox}>
                                    <Box className={classes.tipBodyBox}>
                                        <Typography
                                            variant={TypographyVariants.H6}
                                            className={classes.tipBody1}
                                        >
                                            {body1}
                                        </Typography>
                                        <Typography
                                            variant={TypographyVariants.H6}
                                        >
                                            {body2}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Box
                        style={{
                            height: '800px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default DrinkBaselineOutro;
